import React, { useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  LanguageIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useDismissOnOutsideClick } from '../../../../../hooks/useDismissOnOutsideClick';
import { availableTranslationLanguages } from '../../../../../constants/reportTranslationLanguages';
import { isRunningStatus } from '../../../../../constants/jobStatus';

/** Language switcher for the official report: switch, translate, retry, cancel or delete translations. */
export default function ReportLanguageMenu({ report, primaryLanguage }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { lang, languages, translatingCode } = report;
  const translating = Boolean(translatingCode);

  useDismissOnOutsideClick(ref, open, () => setOpen(false));

  const act = (fn) => (e) => {
    e.stopPropagation();
    fn();
    setOpen(false);
  };

  const completed = languages.filter((l) => l.status === 'COMPLETED');
  const failed = languages.filter((l) => l.status === 'FAILED');
  const running = languages.filter((l) => isRunningStatus(l.status));
  const targets = availableTranslationLanguages(primaryLanguage)
    .filter(({ code }) => !languages.some((l) => l.code === code && l.status !== 'FAILED'));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm whitespace-nowrap ${translating ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-300 hover:bg-gray-50'}`}
        title={translating ? 'Translating report...' : 'Report language'}
      >
        {translating
          ? <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-600" />
          : <LanguageIcon className="h-4 w-4 text-gray-500" />}
        <span className="font-medium">{(lang || primaryLanguage).toUpperCase()}</span>
        <ChevronDownIcon className="h-3 w-3 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1 max-h-80 overflow-y-auto">
          {completed.map((l) => (
            <div key={l.code} className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 ${l.code === lang ? 'bg-primary-50' : ''}`}>
              <button
                type="button"
                onClick={act(() => report.switchLanguage(l.code))}
                className={`flex-1 text-left flex items-center gap-2 ${l.code === lang ? 'text-primary-700 font-medium' : 'text-gray-700'}`}
              >
                <span>{l.name} ({l.code.toUpperCase()})</span>
                {l.code === lang && <CheckCircleIcon className="h-4 w-4 text-primary-600" />}
                {l.is_original && <span className="text-xs text-gray-400 ml-1">original</span>}
              </button>
              {!l.is_original && (
                <button
                  type="button"
                  onClick={act(() => report.deleteTranslation(l.code))}
                  className="ml-2 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                  title={`Delete ${l.name} translation`}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}

          {failed.map((l) => (
            <div key={l.code} className="px-4 py-2 text-sm flex items-center gap-2 bg-red-50">
              <span className="flex-1 text-red-700">{l.name} — failed</span>
              <button
                type="button"
                onClick={act(() => report.translate(l.code))}
                className="px-2 py-0.5 text-xs rounded border border-red-200 hover:bg-red-100 text-red-700"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={act(() => report.deleteTranslation(l.code))}
                className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                title={`Delete ${l.name} translation`}
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}

          {running.map((l) => (
            <div key={l.code} className="px-4 py-2 text-sm text-gray-400 flex items-center gap-2">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
              <span className="flex-1">{l.name} — translating...</span>
              <button
                type="button"
                onClick={act(() => report.cancelTranslation(l.code))}
                className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                title={`Cancel ${l.name} translation`}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}

          {targets.length > 0 && (
            <div className="border-t border-gray-100 mt-1 pt-1">
              <div className="px-4 py-1.5 text-xs font-medium text-gray-400 uppercase">Translate to...</div>
              <div className="px-3 pb-2 flex gap-1 flex-wrap">
                {targets.map(({ code, name }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={act(() => report.translate(code))}
                    disabled={translating}
                    className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-primary-50 hover:border-primary-300 text-gray-600 hover:text-primary-700 disabled:opacity-50"
                    title={name}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
